import type { QueryClient } from '@tanstack/react-query'
import type {
  MentionInput,
  Message,
  MessageInput,
  Review,
  UserInput,
} from '@skop-app/lib-domain/types'
import { ReviewStatus } from '@skop-app/lib-types'

import { reviewKeys } from '@/services/queries/review.keys'

type ReviewListFilters = Record<
  string,
  {
    eq?: string
    ne?: string
  }
>

type OptimisticMessageInput = MessageInput & {
  createdAt?: string
  updatedAt?: string
}

export type ReviewMutationContext = {
  snapshots: Array<{
    queryKey: readonly unknown[]
    data: unknown
    existed: boolean
  }>
}

type DrawerQueryMode = 'scoped' | 'existingOnly'

type ReviewQueryScope = {
  review?: Partial<Review>
  previousReview?: Partial<Review>
  reviewId?: string
  includeDetail?: boolean
  includeMessages?: boolean
  includeDrawer?: boolean
  includeGrid?: boolean
  includeCount?: boolean
  includeNotifications?: boolean
  drawerQueryMode?: DrawerQueryMode
}

type ResolvedReviewPair = {
  previousReview?: Review
  nextReview?: Review
}

type ScopedReviewQueryKey = readonly unknown[]

const matchesFilter = (
  value: string | undefined,
  filter: { eq?: string; ne?: string } | undefined,
) => {
  if (!filter) return true
  if (typeof filter.eq === 'string') return value === filter.eq
  if (typeof filter.ne === 'string') return value !== filter.ne
  return true
}

const matchesReviewFilters = (review: Review, filters: unknown): boolean => {
  if (!filters || typeof filters !== 'object') return true
  const reviewFilters = filters as ReviewListFilters

  return (
    matchesFilter(review.status, reviewFilters.status) &&
    matchesFilter(review.type, reviewFilters.type) &&
    matchesFilter(review.elementID, reviewFilters.elementID)
  )
}

const upsertReviewInList = ({
  list,
  review,
  shouldInclude,
}: {
  list: Review[]
  review: Review
  shouldInclude: boolean
}) => {
  const exists = list.some((item) => item.id === review.id)

  if (!shouldInclude) {
    return exists ? list.filter((item) => item.id !== review.id) : list
  }

  if (exists) {
    return list.map((item) => (item.id === review.id ? review : item))
  }

  return [review, ...list]
}

const matchesReviewOrganization = (
  cacheOrganizationId: unknown,
  organizationID: string | undefined,
) => {
  if (organizationID === undefined || organizationID === null) {
    return cacheOrganizationId === null || cacheOrganizationId === undefined
  }

  return cacheOrganizationId === organizationID
}

const matchesAnyOrganization = (
  cacheOrganizationId: unknown,
  organizationIds: Set<string | undefined>,
) => {
  for (const organizationID of organizationIds) {
    if (matchesReviewOrganization(cacheOrganizationId, organizationID)) {
      return true
    }
  }

  return false
}

const updatePendingReviewsCount = ({
  current,
  previous,
  next,
  organizationID,
}: {
  current: number
  previous?: Review
  next: Review
  organizationID: string | undefined
}) => {
  const wasPending =
    previous?.status === ReviewStatus.PENDING &&
    previous.organizationID === organizationID
  const isPending =
    next.status === ReviewStatus.PENDING &&
    next.organizationID === organizationID

  if (!wasPending && isPending) return current + 1
  if (wasPending && !isPending) return Math.max(0, current - 1)
  return current
}

export const mergeReview = (
  existing: Partial<Review> | undefined,
  input: Partial<Review>,
): Review => ({ ...existing, ...input }) as Review

const buildOptimisticMessage = ({
  user,
  message,
  mentions,
}: {
  user?: UserInput
  message: OptimisticMessageInput
  mentions?: MentionInput[]
}): Message =>
  ({
    ...message,
    ...(mentions?.length ? { mentions: { items: mentions } } : {}),
    user: { name: user?.name, id: user?.id },
  }) as Message

const appendMessageToItems = (items: Message[], nextMessage: Message) => {
  const exists = items.some((item) => item.id === nextMessage.id)
  if (!exists) return [...items, nextMessage]

  return items.map((item) =>
    item.id === nextMessage.id
      ? ({ ...item, ...nextMessage } as Message)
      : item,
  )
}

const appendMessageToReview = ({
  review,
  nextMessage,
}: {
  review: Review
  nextMessage: Message
}): Review =>
  ({
    ...review,
    ...(typeof nextMessage.createdAt === 'string'
      ? { updatedAt: nextMessage.createdAt }
      : {}),
    messages: {
      ...review.messages,
      items: appendMessageToItems(review.messages?.items ?? [], nextMessage),
    },
  }) as Review

const doesReviewListContainReview = (
  reviews: unknown,
  reviewId: string | undefined,
): reviews is Review[] =>
  Array.isArray(reviews) &&
  Boolean(reviewId) &&
  reviews.some((review) => review?.id === reviewId)

const findCachedReview = ({
  queryClient,
  reviewId,
}: {
  queryClient: QueryClient
  reviewId: string
}): Review | undefined => {
  const detail = queryClient.getQueryData<Review>(reviewKeys.detail(reviewId))
  if (detail) return detail

  const drawerQueries = queryClient.getQueriesData<Review[]>({
    queryKey: reviewKeys.drawerPrefix,
  })

  for (const [, reviews] of drawerQueries) {
    const match = reviews?.find((review) => review.id === reviewId)
    if (match) return match
  }

  return undefined
}

const resolveMutationReviews = ({
  queryClient,
  review,
  previousReview,
  reviewId,
}: {
  queryClient: QueryClient
  review?: Partial<Review>
  previousReview?: Partial<Review>
  reviewId?: string
}): ResolvedReviewPair => {
  const nextReviewId = review?.id ?? previousReview?.id ?? reviewId
  const cachedReview = nextReviewId
    ? findCachedReview({ queryClient, reviewId: nextReviewId })
    : undefined

  return {
    previousReview: previousReview
      ? mergeReview(cachedReview, previousReview)
      : cachedReview,
    nextReview: review ? mergeReview(cachedReview, review) : cachedReview,
  }
}

const pushQueryKey = (
  queryKeys: ScopedReviewQueryKey[],
  queryKey: ScopedReviewQueryKey,
): ScopedReviewQueryKey[] => {
  if (
    queryKeys.some(
      (entry) => JSON.stringify(entry) === JSON.stringify(queryKey),
    )
  ) {
    return queryKeys
  }

  return [...queryKeys, queryKey]
}

const getScopedReviewQueries = ({
  queryClient,
  review,
  previousReview,
  reviewId,
  includeDetail = false,
  includeMessages = false,
  includeDrawer = false,
  includeGrid = false,
  includeCount = false,
  includeNotifications = false,
  drawerQueryMode = 'scoped',
}: {
  queryClient: QueryClient
} & ReviewQueryScope) => {
  const resolved = resolveMutationReviews({
    queryClient,
    review,
    previousReview,
    reviewId,
  })
  const scopedReviewId =
    resolved.nextReview?.id ?? resolved.previousReview?.id ?? reviewId
  const scopedOrganizationIds = new Set<string | undefined>([
    resolved.previousReview?.organizationID,
    resolved.nextReview?.organizationID,
  ])
  const reviewIds = new Set<string>(
    [resolved.previousReview?.id, resolved.nextReview?.id, reviewId].filter(
      (value): value is string => typeof value === 'string' && value.length > 0,
    ),
  )

  let queryKeys: ScopedReviewQueryKey[] = []

  if (includeDetail) {
    reviewIds.forEach((id) => {
      queryKeys = pushQueryKey(queryKeys, reviewKeys.detail(id))
    })
  }

  if (includeMessages) {
    reviewIds.forEach((id) => {
      queryKeys = pushQueryKey(queryKeys, reviewKeys.messages(id))
    })
  }

  const allQueries = queryClient.getQueriesData({ queryKey: reviewKeys.all })

  allQueries.forEach(([queryKey, data]) => {
    if (!Array.isArray(queryKey)) return

    const variant = queryKey[1]

    if (variant === 'drawer' && includeDrawer) {
      const shouldIncludeQuery =
        drawerQueryMode === 'existingOnly'
          ? doesReviewListContainReview(data, scopedReviewId)
          : matchesAnyOrganization(queryKey[2], scopedOrganizationIds)

      if (shouldIncludeQuery) {
        queryKeys = pushQueryKey(queryKeys, queryKey)
      }
      return
    }

    if (
      variant === 'grid' &&
      includeGrid &&
      matchesAnyOrganization(queryKey[2], scopedOrganizationIds)
    ) {
      queryKeys = pushQueryKey(queryKeys, queryKey)
      return
    }

    if (
      variant === 'count' &&
      includeCount &&
      matchesAnyOrganization(queryKey[2], scopedOrganizationIds)
    ) {
      queryKeys = pushQueryKey(queryKeys, queryKey)
      return
    }

    if (
      variant === 'notifications' &&
      includeNotifications &&
      matchesAnyOrganization(queryKey[2], scopedOrganizationIds)
    ) {
      queryKeys = pushQueryKey(queryKeys, queryKey)
    }
  })

  return queryKeys
}

export const collectReviewMutationContext = ({
  queryClient,
  review,
  previousReview,
  reviewId,
  includeDetail = true,
  includeMessages = true,
  includeDrawer = false,
  includeGrid = false,
  includeCount = false,
  includeNotifications = false,
  drawerQueryMode = 'scoped',
}: {
  queryClient: QueryClient
} & ReviewQueryScope): ReviewMutationContext => ({
  snapshots: getScopedReviewQueries({
    queryClient,
    review,
    previousReview,
    reviewId,
    includeDetail,
    includeMessages,
    includeDrawer,
    includeGrid,
    includeCount,
    includeNotifications,
    drawerQueryMode,
  }).map((queryKey) => ({
    queryKey,
    data: queryClient.getQueryData(queryKey),
    existed: queryClient.getQueryState(queryKey) !== undefined,
  })),
})

export const restoreReviewMutationContext = ({
  queryClient,
  context,
}: {
  queryClient: QueryClient
  context?: ReviewMutationContext
}) => {
  context?.snapshots.forEach(({ queryKey, data, existed }) => {
    if (!existed) {
      queryClient.removeQueries({
        queryKey,
        exact: true,
      })
      return
    }

    queryClient.setQueryData(queryKey, data)
  })
}

export const cancelReviewQueries = async ({
  queryClient,
  review,
  previousReview,
  reviewId,
  includeDetail = true,
  includeMessages = true,
  includeDrawer = false,
  includeGrid = false,
  includeCount = false,
  includeNotifications = false,
  drawerQueryMode = 'scoped',
}: {
  queryClient: QueryClient
} & ReviewQueryScope) => {
  const scopedQueries = getScopedReviewQueries({
    queryClient,
    review,
    previousReview,
    reviewId,
    includeDetail,
    includeMessages,
    includeDrawer,
    includeGrid,
    includeCount,
    includeNotifications,
    drawerQueryMode,
  })

  await Promise.all(
    scopedQueries.map((queryKey) =>
      queryClient.cancelQueries({
        queryKey,
        exact: true,
      }),
    ),
  )
}

export const applyOptimisticReviewChange = ({
  queryClient,
  mode: _mode,
  input,
}: {
  queryClient: QueryClient
  mode: 'create' | 'update'
  input: Partial<Review>
}) => {
  const { previousReview, nextReview } = resolveMutationReviews({
    queryClient,
    review: input,
    reviewId: input.id,
  })
  if (!nextReview) return

  const touchedOrganizationIds = new Set<string | undefined>([
    previousReview?.organizationID,
    nextReview.organizationID,
  ])
  const reviewQueries = queryClient.getQueriesData<unknown>({
    queryKey: reviewKeys.all,
  })

  if (nextReview.id) {
    queryClient.setQueryData(
      reviewKeys.detail(nextReview.id),
      (cached?: Review) => mergeReview(cached, nextReview),
    )
  }

  reviewQueries.forEach(([queryKey]) => {
    if (!Array.isArray(queryKey)) return
    if (!matchesAnyOrganization(queryKey[2], touchedOrganizationIds)) return

    const variant = queryKey[1]

    if (variant === 'drawer') {
      const filters = queryKey[3]
      queryClient.setQueryData(queryKey, (data: Review[] = []) =>
        upsertReviewInList({
          list: data,
          review: nextReview,
          shouldInclude:
            matchesReviewOrganization(queryKey[2], nextReview.organizationID) &&
            matchesReviewFilters(nextReview, filters),
        }),
      )
      return
    }

    if (variant === 'grid') {
      queryClient.setQueryData(queryKey, (data: Review[] = []) =>
        upsertReviewInList({
          list: data,
          review: nextReview,
          shouldInclude:
            matchesReviewOrganization(queryKey[2], nextReview.organizationID) &&
            nextReview.status === ReviewStatus.PENDING,
        }),
      )
      return
    }

    if (variant === 'count') {
      const organizationID =
        typeof queryKey[2] === 'string' ? queryKey[2] : undefined
      queryClient.setQueryData(queryKey, (data: number = 0) =>
        updatePendingReviewsCount({
          current: data,
          previous: previousReview,
          next: nextReview,
          organizationID,
        }),
      )
    }
  })
}

export const applyOptimisticReviewMessage = ({
  queryClient,
  reviewId,
  message,
  mentions,
  user,
  seedReview,
}: {
  queryClient: QueryClient
  reviewId: string
  message: OptimisticMessageInput
  mentions?: MentionInput[]
  user?: UserInput
  seedReview?: Review
}) => {
  const nextMessage = buildOptimisticMessage({ user, message, mentions })
  const fallbackReview =
    seedReview ?? findCachedReview({ queryClient, reviewId })

  const drawerQueries = queryClient.getQueriesData<Review[]>({
    queryKey: reviewKeys.drawerPrefix,
  })

  drawerQueries.forEach(([queryKey, data]) => {
    if (
      !Array.isArray(queryKey) ||
      !doesReviewListContainReview(data, reviewId)
    ) {
      return
    }

    queryClient.setQueryData(queryKey, (reviews: Review[] = []) =>
      reviews.map((review) =>
        review.id === reviewId
          ? appendMessageToReview({ review, nextMessage })
          : review,
      ),
    )
  })

  const gridQueries = queryClient.getQueriesData<Review[]>({
    queryKey: reviewKeys.gridPrefix,
  })

  gridQueries.forEach(([queryKey, data]) => {
    if (
      !Array.isArray(queryKey) ||
      !doesReviewListContainReview(data, reviewId)
    ) {
      return
    }

    queryClient.setQueryData(queryKey, (reviews: Review[] = []) =>
      reviews.map((review) =>
        review.id === reviewId
          ? appendMessageToReview({ review, nextMessage })
          : review,
      ),
    )
  })

  queryClient.setQueryData(reviewKeys.detail(reviewId), (data?: Review) => {
    const sourceReview = data ?? fallbackReview
    return sourceReview
      ? appendMessageToReview({ review: sourceReview, nextMessage })
      : data
  })

  queryClient.setQueryData<Message[]>(
    reviewKeys.messages(reviewId),
    (data = []) => appendMessageToItems(data, nextMessage),
  )
}

export const revalidateReviewQueries = async ({
  queryClient,
  review,
  previousReview,
  reviewId,
  includeDetail = true,
  includeMessages = true,
  includeDrawer = false,
  includeGrid = false,
  includeCount = false,
  includeNotifications = false,
  refetchDetail = false,
  drawerQueryMode = 'scoped',
}: {
  queryClient: QueryClient
  review?: Partial<Review>
  previousReview?: Partial<Review>
  reviewId?: string
  includeDetail?: boolean
  includeMessages?: boolean
  includeDrawer?: boolean
  includeGrid?: boolean
  includeCount?: boolean
  includeNotifications?: boolean
  refetchDetail?: boolean
  drawerQueryMode?: DrawerQueryMode
}) => {
  try {
    const scopedQueries = getScopedReviewQueries({
      queryClient,
      review,
      previousReview,
      reviewId,
      includeDetail,
      includeMessages,
      includeDrawer,
      includeGrid,
      includeCount,
      includeNotifications,
      drawerQueryMode,
    })

    await Promise.all(
      scopedQueries.map((queryKey) => {
        const variant = Array.isArray(queryKey) ? queryKey[1] : undefined
        const refetchType =
          variant === 'detail' ? (refetchDetail ? 'active' : 'none') : 'none'

        return queryClient.invalidateQueries({
          queryKey,
          exact: true,
          refetchType,
        })
      }),
    )
  } catch (error) {
    console.error('Failed to revalidate review queries', error)
  }
}

export const applyCreatedReviewCaches = (
  queryClient: QueryClient,
  review: Partial<Review>,
) => {
  applyOptimisticReviewChange({
    queryClient,
    mode: 'create',
    input: review,
  })
}
